import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Clock, Gauge, Users, Ship, Check, Trophy, ExternalLink } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { useAuth } from "../../lib/auth";
import { rateOf, eur, fmtDate } from "../../lib/api";
import { CapacityBadge, Exclusive } from "../../components/site/EventCard";
import { EnrollDialog } from "./EnrollDialog";
import { PaymentMethods } from "../../components/site/PayPalPay";
import { Img, GEN, classFallback, classesOf } from "../../components/site/Img";

const SECTIONS = ["level", "how_to_register", "training_program", "accommodation", "food", "parents"];

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon size={18} className="mt-0.5 text-[#00A8B5]" />
    <div><p className="text-xs font-semibold uppercase tracking-wider text-white/50">{label}</p><p className="font-semibold text-white">{value}</p></div>
  </div>
);

const CharterCard = ({ c, e }) => {
  const { t, tr } = useLang();
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid={`charter-offer-${c.id}`}>
      {c.photo && <Img src={c.photo} fallback={GEN.charter} alt={c.model} className="h-44 w-full object-cover" />}
      <div className="space-y-3 p-6">
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-xs font-bold uppercase text-[#00A8B5]">{c.class_name} · {c.boat_type}</p><h4 className="font-display text-lg font-bold text-[#0A2540]">{c.model}</h4></div>
          <p className="whitespace-nowrap text-right font-display text-xl font-extrabold text-[#FF5722]">{eur(c.price)}<span className="block text-xs font-medium text-slate-500">{c.price_unit === "day" ? t("per_day") : t("per_event")}{rateOf(c, e.tax_rate) ? ` + ${rateOf(c, e.tax_rate)}% ${t("vat")}` : ""}</span></p>
        </div>
        <p className="text-sm text-slate-600">{tr(c, "features")}</p>
        {c.equipment_included && <p className="text-sm"><b>{t("included")}:</b> {tr(c, "equipment_included")}</p>}
        {c.deposit > 0 && <p className="text-sm"><b>{t("deposit_charter")}:</b> {eur(c.deposit)}</p>}
        {c.conditions && <p className="text-xs text-slate-500"><b>{t("conditions")}:</b> {tr(c, "conditions")}</p>}
        {c.notes && <p className="text-xs text-slate-500">{tr(c, "notes")}</p>}
      </div>
    </div>
  );
};

const EnrollBox = ({ e, onEnroll }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const open = e.status === "Registrations open" && (!e.capacity.full || e.capacity.waitlist_open);
  return (
    <div className="sticky top-28 space-y-5 rounded-3xl border border-slate-200 bg-white p-7 shadow-xl" data-testid="enroll-box">
      <p className="eyebrow">{t("costs")}</p>
      <p className="font-display text-4xl font-extrabold text-[#0A2540]" data-testid="event-fee">{eur(e.fee)}</p>
      <p className="-mt-3 text-sm text-slate-500">{t("fee")}{e.tax_rate ? ` + ${e.tax_rate}% ${t("vat")}` : ""}</p>
      {e.organizer && <p className="text-sm text-slate-600" data-testid="event-organizer">{t("organizer")}: <b>{e.organizer}</b></p>}
      {e.deposit_amount > 0 && <p className="text-sm text-slate-600">{t("deposit")}: <b>{eur(e.deposit_amount)}</b> · {t("balance")}: <b>{eur(e.fee * (1 + (e.tax_rate || 0) / 100) - e.deposit_amount)}</b></p>}
      {e.services?.length > 0 && <div className="text-sm"><p className="font-semibold text-[#0A2540]">{t("services_avail")}</p>{e.services.map((s) => <p key={s.name} className="flex justify-between text-slate-600" data-testid={`event-service-${s.name}`}><span>{s.name}</span><span>{eur(s.price)}{e.tax_rate ? ` + ${e.tax_rate}%` : ""}</span></p>)}</div>}
      <CapacityBadge cap={e.capacity} />
      {!open ? <p className="rounded-2xl bg-slate-100 p-4 text-center text-sm font-semibold text-slate-600" data-testid="enroll-closed">{t("reg_closed")}</p>
        : user && user.role !== "admin" ? <button onClick={onEnroll} className="btn-buoy w-full justify-center" data-testid="enroll-open-dialog">{e.capacity.full ? t("waitlist") : t("enroll_now")}</button>
        : !user && <div className="space-y-3">
            <Link to={`/register?event=${e.id}`} className="btn-buoy w-full justify-center" data-testid="enroll-register-cta">{t("register_to_enroll")}</Link>
            <Link to={`/login?next=/clinics/${e.id}`} className="block text-center text-sm font-semibold text-[#0A2540] underline" data-testid="enroll-login-cta">{t("login_to_enroll")}</Link>
          </div>}
    </div>
  );
};

export default function EventDetail() {
  const { id } = useParams();
  const { t, tr, ts, lang } = useLang();
  const { data: e, reload } = useFetch(`/events/${id}`);
  const [enroll, setEnroll] = useState(false);
  const { data: types } = useFetch("/public/event_types", { cached: true });
  if (e === undefined) return <div className="h-screen" />;
  if (!e) return <div className="container-x py-40 text-center">Event not found</div>;
  const head = e.instructors?.find((i) => i.id === e.head_coach_id);
  const typeDoc = (types || []).find((x) => x.name === e.type);
  const typeLabel = typeDoc ? tr(typeDoc, "name") : ts(e.type);

  return (
    <div data-testid="event-detail-page">
      <section className="grain relative overflow-hidden bg-[#0A2540] pb-16 pt-36">
        <Img src={e.image} fallback={classFallback(e.class_name)} className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-transparent" />
        <div className="container-x relative">
          <div className="rise flex flex-wrap gap-2">{classesOf(e).map((c) => <span key={c} className="rounded-full bg-[#00A8B5] px-3 py-1 text-xs font-bold text-white">{c}</span>)}<span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur" data-testid="event-type-badge">{typeLabel}</span></div>
          {e.by_invitation && <Exclusive className="rise mt-5 !text-sm" />}
          <h1 className="rise d1 mt-5 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="event-title">{tr(e, "title")}</h1>
          <div className="rise d2 mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Fact icon={CalendarDays} label={t("when")} value={`${fmtDate(e.start_date, lang)} – ${fmtDate(e.end_date, lang)}`} />
            <Fact icon={MapPin} label={t("where")} value={e.location?.name} />
            <Fact icon={Clock} label={t("deadline")} value={fmtDate(e.reg_deadline, lang)} />
            <Fact icon={Users} label={t("head_coach")} value={head?.name || "-"} />
          </div>
        </div>
      </section>
      <section className="container-x grid gap-12 py-16 lg:grid-cols-12">
        <div className="space-y-14 lg:col-span-8">
          <div><p className="eyebrow">{t("description")}</p><p className="mt-4 whitespace-pre-line text-lg leading-relaxed text-slate-700">{tr(e, "description")}</p></div>
          {e.race?.name && (
            <div className="grain relative overflow-hidden rounded-3xl bg-[#0A2540] p-8 text-white" data-testid="event-race-block">
              <p className="eyebrow flex items-center gap-2"><Trophy size={14} />{t("race")}</p>
              <h3 className="mt-3 font-display text-2xl font-extrabold md:text-3xl" data-testid="event-race-name">{tr(e, "race.name")}</h3>
              {e.race.start_date && <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#00A8B5]" data-testid="event-race-dates"><CalendarDays size={15} />{fmtDate(e.race.start_date, lang)}{e.race.end_date && ` – ${fmtDate(e.race.end_date, lang)}`}</p>}
              {e.race.text && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-white/75">{tr(e, "race.text")}</p>}
              {e.race.link && <a href={e.race.link} target="_blank" rel="noreferrer" className="btn-buoy mt-6 w-fit" data-testid="event-race-link">{t("race_info")}<ExternalLink size={15} /></a>}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {SECTIONS.filter((k) => e.sections?.[k]).map((k) => (
              <div key={k} className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={`event-section-${k}`}>
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-[#0A2540]">{k === "level" && <Gauge size={18} className="text-[#FF5722]" />}{t(k)}</h3>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-600">{tr(e, `sections.${k}`)}</p>
              </div>
            ))}
          </div>
          <div>
            <p className="eyebrow">{t("coaches")}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {e.instructors?.map((i) => (
                <div key={i.id} data-testid={`event-coach-${i.id}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#0A2540] to-[#00A8B5] font-bold text-white">{i.photo ? <img src={i.photo} alt="" className="h-full w-full object-cover" /> : i.name.split(" ").map((x) => x[0]).join("")}</div>
                  <div><p className="font-bold text-[#0A2540]">{i.name}{i.id === e.head_coach_id && <span className="ml-2 rounded-full bg-[#FF5722] px-2 py-0.5 text-[10px] text-white">{t("head_coach_badge")}</span>}</p><p className="text-xs text-slate-500">{tr(i, "role")}</p></div>
                </div>
              ))}
            </div>
          </div>
          {e.location && (
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white md:flex">
              {e.location.image && <Img src={e.location.image} fallback={GEN.marina} className="h-56 w-full object-cover md:w-2/5" />}
              <div className="p-6"><p className="eyebrow">{t("location")}</p><h3 className="mt-2 font-display text-xl font-bold text-[#0A2540]">{e.location.name}</h3><p className="text-sm text-slate-500">{e.location.address}</p><p className="mt-3 text-sm text-slate-600">{tr(e.location, "description")}</p>
                {/^https:\/\//.test(e.location.maps_url || "") && <a href={e.location.maps_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-[#0A2540] px-4 py-2 text-sm font-semibold text-[#0A2540] transition-colors hover:bg-[#0A2540] hover:text-white" data-testid="event-maps-link"><MapPin size={15} />{t("open_maps")}</a>}</div>
            </div>
          )}
          {e.charters?.length > 0 && (
            <div><p className="eyebrow flex items-center gap-2"><Ship size={14} />{t("charter_offers")}</p><div className="mt-6 grid gap-6 md:grid-cols-2">{e.charters.map((c) => <CharterCard key={c.id} c={c} e={e} />)}</div></div>
          )}
          {e.show_payment_methods && <PaymentMethods methods={e.payment_methods} eventId={e.id} />}
        </div>
        <div className="lg:col-span-4"><EnrollBox e={e} onEnroll={() => setEnroll(true)} /></div>
      </section>
      {enroll && <EnrollDialog e={e} onClose={() => setEnroll(false)} onDone={reload} />}
    </div>
  );
}
